import { ReloadOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { Alert, Button, Collapse, Space, Tag, Typography } from 'antd';
import { aiChatPromptPreviewApi } from '../../../api/aiAssistant';

const PROMPT_BOX_STYLE: React.CSSProperties = {
  margin: 0,
  padding: 12,
  maxHeight: 320,
  overflow: 'auto',
  background: '#fafafa',
  border: '1px solid #f0f0f0',
  borderRadius: 4,
  fontSize: 12,
  whiteSpace: 'pre-wrap',
};

interface Props {
  /** 保存配置后由外层触发刷新，让预览跟上最新技能 */
  refreshToken?: number;
}

/**
 * 内置 System Prompt 与实际生效 prompt 的只读展示。
 * 数据来自后端同一套拼装逻辑（AiSystemPromptBuilder），不在前端重拼，避免两边漂移。
 */
export default function AiChatPromptPreview({ refreshToken }: Props) {
  const { data, loading, error, refresh } = useRequest(() => aiChatPromptPreviewApi.get(), {
    refreshDeps: [refreshToken],
  });

  return (
    <div data-testid="ai-prompt-preview">
      {error && (
        <Alert
          type="error"
          showIcon
          message="System Prompt 预览加载失败"
          description={error.message || '请点击刷新重试'}
          style={{ marginBottom: 12 }}
        />
      )}
      <Space style={{ marginBottom: 8 }} wrap>
        <Tag color={data?.usingCustomPrompt ? 'orange' : 'blue'}>
          {data?.usingCustomPrompt ? '已使用自定义提示词' : '使用内置提示词'}
        </Tag>
        <Tag>已开启技能 {data?.enabledSkillCount ?? 0} 个</Tag>
        <Tag>示例提问 {data?.starterQuestions?.length ?? 0} 条</Tag>
        {/* 名字要和卡片自带的「刷新」区分开，避免两个同名按钮 */}
        <Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={refresh}>
          刷新预览
        </Button>
      </Space>
      <Collapse
        size="small"
        items={[
          {
            key: 'builtin',
            label: '内置 System Prompt（自定义留空时使用这份）',
            children: <pre style={PROMPT_BOX_STYLE}>{data?.builtinSystemPrompt || '加载中…'}</pre>,
          },
          {
            key: 'effective',
            label: '当前实际下发的完整 Prompt（含技能与运行时约束）',
            children: (
              <>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  数据权限段落按示意范围渲染，真实对话时会替换成当前用户的可见范围。
                </Typography.Text>
                <pre style={{ ...PROMPT_BOX_STYLE, marginTop: 8 }}>
                  {data?.effectiveSystemPrompt || '加载中…'}
                </pre>
              </>
            ),
          },
          {
            key: 'starters',
            label: '空会话示例提问',
            children: (
              <Space direction="vertical" size={4}>
                {(data?.starterQuestions || []).map((starter) => (
                  <Space key={`${starter.skillName || ''}-${starter.question}`}>
                    <Tag color={starter.skillName ? 'green' : 'default'}>
                      {starter.skillName || '内置'}
                    </Tag>
                    <Typography.Text>{starter.question}</Typography.Text>
                  </Space>
                ))}
              </Space>
            ),
          },
        ]}
      />
    </div>
  );
}
